import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ApiError, deleteJSON } from '@/lib/api'
import type { MarketingOptOut } from '@/lib/contacts/types'

export function ContactMarketingOptOuts({
  contactId,
  marketingOptOuts,
  onCleared,
}: {
  contactId: number
  marketingOptOuts: MarketingOptOut[]
  onCleared: () => void
}) {
  const [selected, setSelected] = React.useState<MarketingOptOut | null>(null)
  const [reason, setReason] = React.useState('')
  const clearance = useMutation({
    mutationFn: () =>
      deleteJSON(`/api/v1/contacts/${contactId}/marketing_opt_outs/${selected?.id}`, { reason }),
    onSuccess: () => {
      setSelected(null)
      setReason('')
      onCleared()
    },
  })

  function choose(marketingOptOut: MarketingOptOut) {
    clearance.reset()
    setReason('')
    setSelected(marketingOptOut)
  }

  return (
    <>
      <div className="col-span-full flex min-w-0 flex-col gap-0.5">
        <dt className="text-caption uppercase tracking-wide text-muted-foreground">
          Marketing Opt-out
        </dt>
        <dd className="text-body-sm text-foreground">
          {marketingOptOuts.length === 0 ? (
            'Not opted out'
          ) : (
            <ul className="space-y-1.5">
              {marketingOptOuts.map((marketingOptOut) => (
                <li key={marketingOptOut.id} className="flex flex-wrap items-center gap-2">
                  <Badge variant="neutral">Opted out</Badge>
                  <span className="min-w-0 break-words">{marketingOptOut.email_address}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="whitespace-nowrap"
                    onClick={() => choose(marketingOptOut)}
                  >
                    Clear Marketing Opt-out
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </dd>
      </div>

      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear Marketing Opt-out</DialogTitle>
            <DialogDescription>{selected?.email_address}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="marketing-opt-out-reason">Reason</Label>
            <Textarea
              id="marketing-opt-out-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          {clearance.error && <Alert>{clearanceError(clearance.error)}</Alert>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSelected(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={reason.trim().length === 0 || clearance.isPending}
              onClick={() => clearance.mutate()}
            >
              Clear opt-out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function clearanceError(error: Error) {
  if (error instanceof ApiError && error.status === 422) return 'Reason is required.'
  if (error instanceof ApiError && error.status === 404)
    return 'Marketing Opt-out no longer exists.'
  return 'Marketing Opt-out could not be cleared. Try again.'
}
